import React, { type ReactNode, useEffect, useRef } from 'react'
import { Divider, ListItem, Paragraph, ResponsiveTableRow, Row, Separator, Skeleton } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { getSessionId } from '@fs/zion-session'
import { Link } from '@fs/zion-router'
import { type GroupSummary, type TreeId } from '@fs/zion-tree-types'
import noGroupImg from '../upload-page/assets/no-group.jpg'
import ProcessingFileText from '../upload-page/upload-row/ProcessingFileText'
import getTreeUrl from '../../../helpers/getTreeUrl'
import { useTreeDetails } from '../upload-page/upload-row/useTreeDetails'
import { useShowLoadingBar } from '../upload-page/upload-row/useShowLoadingBar'
import CetUploadError from '../upload-page/upload-row/CetUploadError'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import ManageTreeRowActions, { ManageTreeRowActionsSkeleton } from './ManageTreeRowActions'
import { formatDate } from '../../helpers'
import { useTreeRole } from '../../../helpers/useTreeRole'

const imageStyleCss = css`
  img {
    border-radius: 8px;
    max-width: 79px;
  }
`

const linkStyleCss = css`
  text-decoration: none;
`

function TableRowName({ children, src }: { children: ReactNode; src: string }): JSX.Element {
  return (
    <Row alignItemsY="middle" gutters="xs">
      <div css={imageStyleCss} noShrinkX>
        <img src={src} alt="" />
      </div>
      {children}
    </Row>
  )
}

type GetTestIdParams = {
  isResearcherTree: boolean
  treeId?: TreeId | null
  groupId?: string
  fileName?: string
}

const getTestId = ({ isResearcherTree, treeId, groupId, fileName }: GetTestIdParams): string => {
  const treeType = isResearcherTree ? 'cet' : 'family-group'
  const listId = treeId || groupId || fileName
  const suffix = listId ? `:${listId}` : ''
  return `manage-tree-list:${treeType}${suffix}`
}

type ManageTreeRowProps =
  | {
      asTableRow?: boolean
      group: GroupSummary
      file?: GedcomFile
      failedFile?: never
      processingFile?: never
      isMobile: boolean
      isLast?: boolean
      showCombinedDesign?: boolean
      currentUserCisId?: string
    }
  | {
      asTableRow?: boolean
      group?: never
      file?: never
      failedFile?: never
      processingFile: GedcomFile
      isMobile: boolean
      isLast?: boolean
      showCombinedDesign?: boolean
      currentUserCisId?: never
    }
  | {
      asTableRow?: boolean
      group?: never
      file?: never
      failedFile: GedcomFileFailed
      processingFile?: never
      isMobile: boolean
      isLast?: boolean
      showCombinedDesign?: boolean
      currentUserCisId?: never
    }

function ManageTreeRow({
  asTableRow,
  group,
  file,
  failedFile,
  processingFile,
  isMobile,
  isLast,
  showCombinedDesign,
  currentUserCisId,
}: ManageTreeRowProps): JSX.Element {
  const [t] = useTranslation()

  // Extract values for hooks (must be called before any conditional returns)
  const treeId = group?.treeIds?.[0]
  const fileStatus = file?.fileStatus ?? processingFile?.fileStatus
  const percentComplete = file?.percentComplete ?? processingFile?.percentComplete

  const sessionId = getSessionId()
  const { treeDetails, refetchTreeDetails } = useTreeDetails(treeId)
  const [{ isImporting, isProcessing }] = useShowLoadingBar({ fileStatus, percentComplete })
  const prevFileStatusRef = useRef(fileStatus)
  const modifiedDate = treeDetails?.updateDate || file?.modified
  const formattedDate = modifiedDate ? formatDate(modifiedDate, 'MM/dd/yyyy') : undefined

  const roleLabel = useTreeRole({
    isResearcherTree: group?.types?.includes('RESEARCHER_TREE'),
    memberCisId: currentUserCisId,
    memberStatus: group?.memberStatus,
    ownerCisId: treeDetails?.ownerCisId,
  })

  // Refetch tree details when import completes to get updated privacy status
  useEffect(() => {
    const prevStatus = prevFileStatusRef.current
    prevFileStatusRef.current = fileStatus

    if (prevStatus === 'IMPORTING' && fileStatus === 'IMPORTED' && treeId) {
      refetchTreeDetails()
    }
  }, [fileStatus, treeId, refetchTreeDetails])

  if (failedFile) {
    const imageSrc = noGroupImg
    const failedTestId = `manage-tree-list:failed-file:${failedFile.fileName}`
    const uploadError = <CetUploadError fileName={failedFile.fileName} fileStatus={failedFile.fileStatus} />

    if (asTableRow) {
      return (
        <>
          {/* A failed upload has no date, role, or tools to report, so only the name column is filled. */}
          <ResponsiveTableRow
            data-testid={failedTestId}
            noBottomDivider
            column1={<TableRowName src={imageSrc}>{failedFile.fileName}</TableRowName>}
          />
          {/* The alert spans all four columns; keep this in step with the table's `totalColumnCount`. */}
          <ResponsiveTableRow column1span={4} column1={uploadError} />
        </>
      )
    }

    return (
      <>
        <ListItem
          data-testid={failedTestId}
          dense={isMobile}
          startElement={
            <div css={imageStyleCss}>
              <ListItem.Image size="md" src={imageSrc} />
            </div>
          }
          primaryText={failedFile.fileName}
        />
        {uploadError}
      </>
    )
  }

  if (processingFile) {
    const imageSrc = noGroupImg
    const processingFileIsProcessing = isProcessing || isImporting
    const processingTestId = `manage-tree-list:processing-file:${processingFile.fileName}`
    const processingName = (
      <>
        {processingFile.title}
        <ProcessingFileText />
      </>
    )
    // No `showCombinedDesign` or `asTableRow` here: a processing row has no group, so `CetActions`
    // renders nothing to stack, and its progress bar sits in a full-width row that wants the wider size.
    const processingActions = (
      <ManageTreeRowActions
        file={processingFile}
        group={undefined}
        isProcessingOrLoading={processingFileIsProcessing}
        isPrivateTree={undefined}
        isMobile={isMobile}
      />
    )

    const showProgressBar = processingFile.fileStatus === 'IMPORTING' && processingFile.percentComplete != null

    if (asTableRow) {
      // The progress bar is too wide for the Tools column, so it takes a full-width row instead.
      let toolsCell: JSX.Element | undefined
      if (!showProgressBar) toolsCell = processingActions

      return (
        <>
          <ResponsiveTableRow
            data-testid={processingTestId}
            noBottomDivider={showProgressBar}
            column1={<TableRowName src={imageSrc}>{processingName}</TableRowName>}
            column4={toolsCell}
          />
          {showProgressBar && <ResponsiveTableRow column1span={4} column1={processingActions} />}
        </>
      )
    }

    return (
      <>
        <ListItem
          data-testid={processingTestId}
          dense={isMobile}
          startElement={
            <div css={imageStyleCss}>
              <ListItem.Image size="md" src={imageSrc} />
            </div>
          }
          primaryText={processingName}
          endElement={!isMobile && processingActions}
        />
        {isMobile && (
          <>
            {showProgressBar && (
              <>
                <Separator size="pico" />
                {processingActions}
              </>
            )}

            {!isLast && <Separator size="sm" />}
          </>
        )}
      </>
    )
  }

  // group is guaranteed to be defined if failedFile and processingFile are not
  const name = group.name
  const groupId = group.id
  const types = group.types
  const imageUrl = group.imageUrl
  const fileName = file?.fileName

  const imageSrc = imageUrl ? `${imageUrl}?sessionId=${sessionId}` : noGroupImg
  const isProcessingOrLoading = isProcessing || isImporting
  const isPrivateTree = treeDetails?.private
  const isResearcherTree = types?.includes('RESEARCHER_TREE')

  const treeUrl = getTreeUrl({
    treeId,
    mePersonId: treeDetails?.startingPersonId,
  })

  const testId = getTestId({ isResearcherTree, treeId, groupId, fileName })

  function getPrimaryText(): JSX.Element | string {
    if (isProcessingOrLoading && !treeId) {
      return (
        <>
          {name}
          <ProcessingFileText />
        </>
      )
    }
    return (
      <Link css={linkStyleCss} to={treeUrl} external wayfinding>
        {name}
      </Link>
    )
  }

  return (
    <>
      {asTableRow ? (
        <ResponsiveTableRow
          data-testid={testId}
          column1={<TableRowName src={imageSrc}>{getPrimaryText()}</TableRowName>}
          column2={formattedDate}
          column3={roleLabel}
          column4={
            isMobile ? undefined : (
              <ManageTreeRowActions
                file={file}
                group={group}
                isProcessingOrLoading={isProcessingOrLoading}
                isPrivateTree={isPrivateTree}
                isMobile={isMobile}
                asTableRow={asTableRow}
                showCombinedDesign={showCombinedDesign}
              />
            )
          }
        />
      ) : (
        <ListItem
          data-testid={testId}
          dense={isMobile}
          startElement={
            // TREEWEB-11486 - We specify role="none" to avoid surfacing images to screen readers that we have not captured functional alt text for.
            <div css={imageStyleCss}>
              <ListItem.Image role="none" size="md" src={imageSrc} />
            </div>
          }
          primaryText={getPrimaryText()}
          endElement={
            isMobile ? undefined : (
              <ManageTreeRowActions
                file={file}
                group={group}
                isProcessingOrLoading={isProcessingOrLoading}
                isPrivateTree={isPrivateTree}
                isMobile={isMobile}
              />
            )
          }
        />
      )}
      {isMobile && (
        <>
          <Separator size="xs" />
          {showCombinedDesign && (
            <>
              {/* <Separator size="sm" /> */}
              <Paragraph size="sm">
                {t('tree-import.manage-trees.table-date-heading')}: {formattedDate}
              </Paragraph>
              <Paragraph size="sm">
                {t('tree-import.manage-trees.table-role-heading')}: {roleLabel}
              </Paragraph>
              <Paragraph size="sm">{t('tree-import.manage-trees.table-tools-heading')}:</Paragraph>
            </>
          )}
          <ManageTreeRowActions
            file={file}
            group={group}
            isProcessingOrLoading={isProcessingOrLoading}
            isPrivateTree={isPrivateTree}
            isMobile={isMobile}
            showCombinedDesign={showCombinedDesign}
          />
          {showCombinedDesign && !isLast && (
            <>
              <Separator size="sm" />
              <Divider size="sm" />
            </>
          )}
          {!isLast && <Separator size="sm" />}
        </>
      )}
    </>
  )
}

export { ManageTreeRow }

export function ManageTreeRowSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <>
      <ListItem
        aria-hidden
        dense={isMobile}
        startElement={<Skeleton.Image width={79} height={56} />}
        primaryText={<Skeleton.TypeSkeleton maxWidth={250} randomSeed={3} />}
        endElement={isMobile ? undefined : <ManageTreeRowActionsSkeleton />}
        data-testid="ManageTreeRowSkeleton"
      />

      {isMobile && (
        <>
          <Separator size="pico" />
          <ManageTreeRowActionsSkeleton isMobile />
        </>
      )}
    </>
  )
}
